import {
  Box,
  Divider,
  Flex,
  Grid,
  GridItem,
  HStack,
  Progress,
  Skeleton,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  VStack,
} from '@chakra-ui/react';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { GetAssignedReceivableQueryGQLType } from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from 'packages/common-frontend/colors';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons';

type StatusProps = {
  icon: React.ReactElement;
  label: string;
  theme: 'success' | 'error';
  width?: string;
};
export const Status = ({ icon, label, theme, width }: StatusProps) => {
  return (
    <Tag
      variant="solid-muted"
      colorScheme={theme === 'success' ? 'green' : 'red'}
      width={width ?? 'max-content'}
    >
      <TagLeftIcon>{icon}</TagLeftIcon>
      <TagLabel>{label}</TagLabel>
    </Tag>
  );
};

type TestsSummaryProps = {
  type: 'eligibility' | 'concentration';
  kickout: number;
  testsFailed: number;
  testsTotal: number;
};
const TestsSummary = ({
  type,
  kickout,
  testsFailed,
  testsTotal,
}: TestsSummaryProps) => {
  const label =
    type === 'eligibility'
      ? 'Total Eligibility Kickout'
      : 'Excess Concentration';

  const allPassed = testsFailed === 0;
  const testsSummaryInfo = () => {
    if (type === 'eligibility') {
      return allPassed
        ? `${testsTotal}/${testsTotal} Tests Passed`
        : `${testsFailed}/${testsTotal} Tests Failed`;
    }
    return `${testsFailed}/${testsTotal} Limits Exceeded`;
  };

  const colorScheme = useMemo(() => {
    if (allPassed) return 'green';
    if (type === 'eligibility') return 'red';
    return 'orange';
  }, [allPassed, type]);

  const backgroundColor = useMemo(() => {
    if (allPassed) return colors.green[100];
    if (type === 'eligibility') return colors.red[100];
    return colors.orange[100];
  }, [allPassed, type]);

  return (
    <Flex
      border="1px"
      borderColor={colors.gray[200]}
      borderRadius="8px"
      padding="16px"
      direction="column"
      width="100%"
    >
      <Text fontSize="14px" color={colors.black[70]}>
        {label}
      </Text>
      <Text mt="8px" fontWeight="semibold">
        {commonUtils.numberToCurrencyWithSymbol({
          number: kickout,
        }) ?? kickout}
      </Text>
      <Progress
        mt="16px"
        value={allPassed ? 100 : (testsFailed / testsTotal) * 100}
        colorScheme={colorScheme}
        size="sm"
        borderRadius="4px"
        backgroundColor={backgroundColor}
      />
      <Text mt="8px" fontSize="14px">
        {testsSummaryInfo()}
      </Text>
    </Flex>
  );
};

const baseFieldTypesToRound = [
  'float',
  'optionalFloat',
  'percentage',
  'optionalPercentage',
  'currency',
  'optionalCurrency',
];

type FieldsSummaryProps = {
  latestRecord: any;
  loanProductMechanic: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities;
  originationDate: Date | null;
};
const FieldsSummary = ({
  latestRecord,
  loanProductMechanic,
  originationDate,
}: FieldsSummaryProps) => {
  const [fieldInfoList, setFieldInfoList] = useState<
    { name: string; value: string }[]
  >([]);

  useEffect(() => {
    const fieldInfo: { name: string; value: string }[] = [];
    fieldInfo.push({
      name: 'origination_date',
      value: originationDate
        ? commonDate.dateToHumanDateString(originationDate)
        : '',
    });
    fieldInfo.push({
      name: 'exposure',
      value:
        commonUtils.numberToCurrencyWithSymbol({
          number: latestRecord['rif.exposure'],
        }) ?? latestRecord['rif.exposure'],
    });
    loanProductMechanic._config_.baseFieldConfigs.forEach((baseField) => {
      const rawValue = latestRecord[`bf.${baseField.field}`];
      let fieldValue: string = String(rawValue);
      if (rawValue === null || rawValue === undefined) {
        fieldValue = '';
      } else if (
        baseField.type === 'date' ||
        baseField.type === 'optionalDate'
      ) {
        fieldValue = commonDate.dateToHumanDateString(
          DateTime.fromISO(rawValue).toJSDate(),
        );
      } else if (baseFieldTypesToRound.includes(baseField.type)) {
        if (typeof rawValue === 'number') {
          fieldValue = `${parseFloat(rawValue.toFixed(3))}`;
        }
      }
      fieldInfo.push({
        name: baseField.field,
        value: fieldValue,
      });
    });
    setFieldInfoList(fieldInfo);
  }, [latestRecord, loanProductMechanic, originationDate]);

  return (
    <Box
      padding="24px"
      borderRadius="6px"
      border="1px"
      borderColor={colors.gray[200]}
      width="80%"
      minWidth="min-content"
    >
      <Grid
        templateColumns="repeat(4, 1fr)"
        templateRows={`repeat(${
          Math.floor(fieldInfoList.length / 4) + 1
        }, 1fr)`}
        rowGap="24px"
        columnGap="32px"
      >
        {fieldInfoList.map((field) => {
          return (
            <GridItem colSpan={1} rowSpan={1} key={field.name}>
              <Flex direction="column">
                <Text fontSize="14px" color={colors.black[70]}>
                  {field.name}
                </Text>
                <Divider mt="8px" mb="8px" color={colors.gray[200]} />
                <Text fontWeight="semibold">{field.value}</Text>
              </Flex>
            </GridItem>
          );
        })}
      </Grid>
    </Box>
  );
};

const calculateEligibilityStats = (
  latestRecord: any,
  loanProductMechanic: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities,
) => {
  let eligibilityTestsFailed = 0;
  let eligibilityTestsTotal = 0;
  const totalEligibilityKickout = latestRecord['ef.eligibilitykickout'];
  loanProductMechanic.eligibilityTests.forEach((eligibilityTest) => {
    if (eligibilityTest._config_.eligibilityTestConfig) {
      eligibilityTestsTotal += 1;
      if (
        latestRecord[
          `ess.${eligibilityTest._config_.eligibilityTestConfig.propertyKey.toLowerCase()}.es`
        ] === 'INELIGIBLE'
      ) {
        eligibilityTestsFailed += 1;
      }
    }
  });
  return {
    eligibilityTestsFailed,
    eligibilityTestsTotal,
    totalEligibilityKickout,
  };
};

const calculateConcentrationStats = (
  latestRecord: any,
  loanProductMechanic: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities,
  multiLoanProductConcentrationLimits: CreditAgreement.DomainEntity.MultiLoanProductConcentrationLimitWithEntities[],
) => {
  let concentrationLimitsExceeded = 0;
  let concentrationLimitsTotal = 0;
  let totalConcentrationKickout = 0;
  [
    ...loanProductMechanic.concentrationLimits,
    ...multiLoanProductConcentrationLimits,
  ].forEach((concentrationLimit) => {
    if (concentrationLimit._config_.concentrationLimitConfig) {
      concentrationLimitsTotal += 1;
      const concentrationKickout =
        latestRecord[
          `clr.${concentrationLimit._config_.concentrationLimitConfig.propertyKey.toLowerCase()}.clk`
        ];
      if (concentrationKickout > 0) {
        concentrationLimitsExceeded += 1;
        totalConcentrationKickout += concentrationKickout;
      }
    }
  });
  return {
    concentrationLimitsExceeded,
    concentrationLimitsTotal,
    totalConcentrationKickout,
  };
};

type ReceivableDataSummaryProps = {
  latestRecord: any;
  originationDate: Date | null;
  loanProductMechanic: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities;
  multiLoanProductConcentrationLimits: CreditAgreement.DomainEntity.MultiLoanProductConcentrationLimitWithEntities[];
};
const ReceivableDataSummary = ({
  latestRecord,
  loanProductMechanic,
  originationDate,
  multiLoanProductConcentrationLimits,
}: ReceivableDataSummaryProps) => {
  const {
    eligibilityTestsFailed,
    eligibilityTestsTotal,
    totalEligibilityKickout,
  } = calculateEligibilityStats(latestRecord, loanProductMechanic);

  const {
    concentrationLimitsExceeded,
    concentrationLimitsTotal,
    totalConcentrationKickout,
  } = calculateConcentrationStats(
    latestRecord,
    loanProductMechanic,
    multiLoanProductConcentrationLimits,
  );

  return (
    <Flex mt="40px" width="100%">
      <FieldsSummary
        latestRecord={latestRecord}
        loanProductMechanic={loanProductMechanic}
        originationDate={originationDate}
      />
      <VStack direction="column" ml="24px" spacing="24px" width="20%">
        <TestsSummary
          type="eligibility"
          kickout={totalEligibilityKickout}
          testsFailed={eligibilityTestsFailed}
          testsTotal={eligibilityTestsTotal}
        />
        <TestsSummary
          type="concentration"
          kickout={totalConcentrationKickout}
          testsFailed={concentrationLimitsExceeded}
          testsTotal={concentrationLimitsTotal}
        />
      </VStack>
    </Flex>
  );
};

type ReceivableStatusesProps = { latestRecord: any };
const ReceivableStatuses = ({ latestRecord }: ReceivableStatusesProps) => {
  const isEligible = latestRecord['ef.iseligible'];
  const isDelinquent = latestRecord['rif.normalizedisdelinquent'];
  const isChargedOff = latestRecord['rif.normalizedischargedoff'];

  return (
    <HStack spacing="16px" ml="16px">
      <Status
        icon={
          <FaIcon
            icon={isEligible ? 'circle-check' : 'circle-xmark'}
            color={isEligible ? colors.green[400] : colors.red[400]}
            size="large"
          />
        }
        label={isEligible ? 'Eligible' : 'Not Eligible'}
        theme={isEligible ? 'success' : 'error'}
      />
      <Status
        icon={
          <FaIcon
            icon={!isDelinquent ? 'circle-check' : 'circle-exclamation'}
            color={!isDelinquent ? colors.green[400] : colors.red[400]}
            size="large"
          />
        }
        label={isDelinquent ? 'Delinquent' : 'Not Delinquent'}
        theme={!isDelinquent ? 'success' : 'error'}
      />
      {isChargedOff && (
        <Status
          icon={
            <FaIcon icon="circle-xmark" color={colors.red[400]} size="large" />
          }
          label="Charged Off"
          theme="error"
        />
      )}
    </HStack>
  );
};

type AssetDetailsSummaryProps = {
  receivable?: GetAssignedReceivableQueryGQLType;
  latestRecord?: any;
  loanProductMechanic?: CreditAgreement.DomainEntity.LoanProductMechanicsWithEntities;
  multiLoanProductConcentrationLimits?: CreditAgreement.DomainEntity.MultiLoanProductConcentrationLimitWithEntities[];
  originationDate: Date | null;
  assignedAtDate: Date | null;
};
export const AssetDetailsSummary = ({
  receivable,
  latestRecord,
  loanProductMechanic,
  multiLoanProductConcentrationLimits,
  originationDate,
  assignedAtDate,
}: AssetDetailsSummaryProps) => {
  return (
    <Box mt="40px">
      {receivable ? (
        <>
          <Flex>
            <Text fontSize="2xl" fontWeight="semibold">
              Asset Details
            </Text>
            {latestRecord && (
              <>
                &nbsp;&nbsp;
                <Text fontSize="2xl" color={colors.black[70]}>
                  as of{' '}
                  {commonDate.dateToHumanDateString(
                    DateTime.fromISO(latestRecord.reportingdate).toJSDate(),
                  )}
                </Text>
              </>
            )}
          </Flex>
          <Flex alignItems="center" mt="40px" justifyContent="space-between">
            <HStack spacing="16px">
              <Tag
                colorScheme="gray"
                variant="outline-muted"
                alignItems="center"
              >
                <TagLeftIcon>
                  <FaIcon icon="building-columns" />
                </TagLeftIcon>
                <TagLabel>
                  {receivable.assignedReceivable.creditFacility.name}
                </TagLabel>
              </Tag>

              <Tag
                colorScheme="gray"
                variant="outline-muted"
                alignItems="center"
              >
                <TagLeftIcon>
                  <FaIcon icon="building" />
                </TagLeftIcon>
                <TagLabel>
                  {
                    receivable.assignedReceivable.creditFacility
                      .borrowerOrganizationName
                  }
                </TagLabel>
              </Tag>

              {loanProductMechanic && (
                <Tag
                  colorScheme="gray"
                  variant="outline-muted"
                  alignItems="center"
                >
                  <TagLeftIcon>
                    <FaIcon icon="shapes" />
                  </TagLeftIcon>
                  <TagLabel>{loanProductMechanic.entity.name}</TagLabel>
                </Tag>
              )}

              {assignedAtDate && (
                <Tag
                  colorScheme="gray"
                  variant="outline-muted"
                  alignItems="center"
                >
                  <TagLeftIcon>
                    <FaIcon icon="calendar" />
                  </TagLeftIcon>
                  <TagLabel>
                    Assigned Date:{' '}
                    {commonDate.dateToHumanDateString(assignedAtDate)}
                  </TagLabel>
                </Tag>
              )}
            </HStack>
            {latestRecord && <ReceivableStatuses latestRecord={latestRecord} />}
          </Flex>
        </>
      ) : (
        <Skeleton
          data-testid="loading-portfolio-receivable-asset-details1"
          height="130px"
          width="100%"
        />
      )}
      {latestRecord &&
      loanProductMechanic &&
      multiLoanProductConcentrationLimits ? (
        <ReceivableDataSummary
          latestRecord={latestRecord}
          loanProductMechanic={loanProductMechanic}
          multiLoanProductConcentrationLimits={
            multiLoanProductConcentrationLimits
          }
          originationDate={originationDate}
        />
      ) : (
        <Flex mt="40px" height="475px" width="100%">
          <Skeleton
            data-testid="loading-portfolio-receivable-asset-details2"
            height="475px"
            width="80%"
          />
          <Flex direction="column" ml="24px" height="475px" width="20%">
            <Skeleton
              data-testid="loading-portfolio-receivable-asset-details3"
              height="150px"
              width="100%"
            />
            <Skeleton
              data-testid="loading-portfolio-receivable-asset-details4"
              height="150px"
              width="100%"
              mt="24px"
            />
          </Flex>
        </Flex>
      )}
    </Box>
  );
};
